import React, { useEffect, useState } from 'react';
import Helmet from 'react-helmet';
import { useMutation } from 'react-apollo';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Card from '@material-ui/core/Card';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Skeleton from '@material-ui/lab/Skeleton';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import Button from 'components/common/Button';
import TextField from 'components/form/Input';
import { TagsInput } from 'components/form/Select';
import { validateEmail } from 'utils';
import SyncContacts from './components/SyncContacts';
import Contacts from './components/Contacts';
import useSnack from 'hooks/useSnack';
import { API } from 'aws-amplify';
import { useSelector } from 'react-redux';
import Loading from 'components/common/Spin';
import InviteModal from './components/InviteModal';
import { INSERT_CONTACTS } from 'graphql/referral/contacts';
import { UPDATE_REFERRAL } from 'graphql/referral';
import { DEFAULT, SOLO_TERMS } from './constants';
import ShareInput from 'components/social-share/ShareInput';
import AddInviteModal from './components/AddInviteModal';
import { hashCode } from 'utils/referralInvite';

const ReferralHome = ({ referral, solo }) => {
  const user = useSelector((s) => s.user.user);
  const profile = useSelector((s) => s.student.profile);
  const [showInvite, setShowInvite] = useState(false);
  const [emails, setEmails] = useState([]);
  const [contact, setContact] = useState();
  const [syncing, setSyncing] = useState(false);
  const [insertContacts] = useMutation(INSERT_CONTACTS);
  const [updateReferral] = useMutation(UPDATE_REFERRAL);
  const snack = useSnack();
  const [shareText, setshareText] = useState(
    'WeAdmit is a method-driven, results-based online college counseling program that will guide your child to their perfect-fit college and help them find long-term career success after graduation. www.weadmit.com',
  );
  const [showAddInvite, setShowAddInvite] = useState(false);

  useEffect(() => {
    setShowAddInvite(true);
  }, []);

  const onManualInvite = () => {
    if (!emails.length) return;
    setShowInvite(true);
    setContact();
    window.analytics.track('Referral.InviteViaEmail.Clicked');
  };

  const onCloseModal = () => {
    setShowInvite(false);
    setShowAddInvite(false);
    setContact();
    window.analytics.track('Referral.InviteViaEmail.Cancelled');
  };

  const onSendInvite = async (message) => {
    let payload = [];
    if (contact) {
      if (contact === 'ALL') {
        payload = contact;
      } else {
        payload.push({
          id: contact.id,
          email: contact.email,
          phone: contact.phone,
          first_name: contact.first_name,
        });
      }
      window.analytics.track(
        contact === 'ALL'
          ? 'Referral.Invited.AllContacts'
          : 'Referral.Invited.Contacts',
        {
          resending: contact.invited,
        },
      );
    } else {
      window.analytics.track('Referral.Invited.Emails', {
        count: emails.length,
      });
      const {
        data: { rows },
      } = await insertContacts({
        variables: {
          data: emails.map((i) => ({
            email: i.value,
            source: 'manual',
            external_id: hashCode(i.value),
            user_id: user.id,
          })),
        },
      });

      payload = rows.returning.map((i) => ({
        id: i.id,
        email: i.email,
      }));
    }
    return API.post('api', '/users/send-notification', {
      body: {
        data: {
          user_id: user.id,
          referral_code: referral.referral_code,
          contacts: payload,
          message,
        },
        skipTemplates: true,
        topic: 'REFERRAL_SEND_INVITE',
      },
    }).then(
      (res) => {
        snack.success('Invitation Sent!');
        setShowInvite(false);
        setEmails([]);
      },
      (e) => snack.error(e.message),
    );
  };

  const onInvite = (contact) => {
    setContact(contact);
    setEmails([]);
    setShowInvite(true);
  };

  const onDisconnect = () => {
    return updateReferral({
      variables: {
        id: referral.id,
        data: {
          google_tokens: null,
        },
      },
    }).then(
      () => {
        snack.success('Google account disconnected.');
        window.analytics.track('Referral.Disconnected');
      },
      (e) => snack.error(e.message),
    );
  };

  const onConnected = () => {
    setSyncing(true);
    return API.post('api', `/users/${user.id}/sync-google-contacts`)
      .then(
        (resp) => {
          snack.success('You contacts successfully synced!');
          window.analytics.track('Referral.Synced', {
            count: resp,
          });
        },
        (e) => {
          snack.error(e.message);
        },
      )
      .finally(() => setSyncing(false));
  };

  const onShareInputChange = (e) => {
    setshareText(e.target.value);
  };

  const TERMS = profile.product_type === 'solo' || solo ? SOLO_TERMS : DEFAULT;
  const refLink = `${process.env.REACT_APP_URL}/free-counseling?ref=${referral.referral_code}`;
  return (
    <Box>
      <Helmet title="Referral Program" />
      <Typography paragraph variant="h5">
        {TERMS.sync.title}
      </Typography>
      <Box maxWidth={600} mb={4}>
        <Typography color="textSecondary">{TERMS.sync.description}</Typography>
      </Box>
      <Card component={Box} p={5} mb={3}>
        <Grid container alignItems="center">
          <Grid item xs={12} sm={5}>
            <Typography gutterBottom variant="h5">
              Add Friends
            </Typography>
            <Typography color="textSecondary">
              Tell your friends about WeAdmit
            </Typography>
          </Grid>
          <Grid item xs={12} sm={7}>
            <Box display="flex">
              <Box flex={1}>
                <TagsInput
                  noMargin
                  placeholder="Enter email address and press Enter"
                  value={emails}
                  onChange={setEmails}
                  validate={validateEmail}
                />
              </Box>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={onManualInvite}
              >
                Add
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Card>
      <Card component={Box} p={5} mb={3}>
        <Grid container alignItems="center">
          <Grid item sm={5}>
            <Typography gutterBottom variant="h5">
              Invite by link
            </Typography>
            <Typography color="textSecondary">
              Copy link and share with your friend(s)
            </Typography>
          </Grid>
          <Grid item xs={12} sm={7}>
            <Box display="flex">
              <Box flex={1}>
                <TextField disableMargin fullWidth readOnly value={refLink} />
              </Box>
              <CopyToClipboard
                text={refLink}
                onCopy={() => snack.success('Link copied!')}
              >
                <Button variant="contained" color="primary" size="large">
                  Copy link
                </Button>
              </CopyToClipboard>
            </Box>
          </Grid>
        </Grid>
      </Card>
      <Card component={Box} p={5} mb={3}>
        <Grid container alignItems="center">
          <Grid item xs={12}>
            <Typography align="center" gutterBottom variant="h5">
              Tell your friends about joining WeAdmit!
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" justifyContent="center">
              <Box>
                <ShareInput
                  onChange={onShareInputChange}
                  multiline
                  fullWidth
                  value={shareText}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Card>
      {referral.google_tokens ? (
        <Loading
          spinning={syncing}
          linear={false}
          tip={'Please wait, your contacts being downloaded...'}
        >
          {syncing ? (
            <Skeleton
              animation="wave"
              variant="rect"
              width="100%"
              height={200}
            />
          ) : (
            <Contacts
              referral={referral}
              syncing={syncing}
              onInvite={onInvite}
              onDisconnect={onDisconnect}
            />
          )}
        </Loading>
      ) : (
        <SyncContacts referral={referral} onConnected={onConnected} />
      )}
      {showInvite && (
        <InviteModal
          emails={emails}
          contact={contact}
          onClose={onCloseModal}
          onSubmit={onSendInvite}
        />
      )}
      {showAddInvite && (
        <AddInviteModal
          referral={referral}
          onClose={onCloseModal}
          onSubmit={() => setShowAddInvite(false)}
        />
      )}

    </Box>
  );
};

export default ReferralHome;
